import { collection, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from './firebase';

export const WORKSPACE_SCHEMA_VERSION = 3;
export const DEFAULT_PROJECT_ID = 'main';
const LOCAL_PROJECTS_KEY = 'gearnode_projects_v2';
const LEGACY_WORKSPACE_KEY = 'gearnode_workspace_v1';

export const EMPTY_WORKSPACE = {
  nodes: [],
  links: [],
  assignedAssetIds: [],
  project: { name: 'Mój pierwszy event', date: '', location: '', formatId: '1080p50' },
};

const projectsCollection = uid =>
  collection(db, 'artifacts', 'gearnode-prod-v1', 'users', uid, 'projects');

const workspaceRef = (uid, projectId) => doc(projectsCollection(uid), projectId);

function readLocalProjects() {
  try {
    const projects = JSON.parse(localStorage.getItem(LOCAL_PROJECTS_KEY));
    if (projects && typeof projects === 'object') return projects;
    const legacy = JSON.parse(localStorage.getItem(LEGACY_WORKSPACE_KEY));
    if (legacy) return { [DEFAULT_PROJECT_ID]: { ...EMPTY_WORKSPACE, ...legacy } };
  } catch {}
  return { [DEFAULT_PROJECT_ID]: EMPTY_WORKSPACE };
}

function writeLocalProjects(projects) {
  localStorage.setItem(LOCAL_PROJECTS_KEY, JSON.stringify(projects));
}

export function listLocalProjects() {
  return Object.entries(readLocalProjects()).map(([id, workspace]) => ({
    id,
    ...EMPTY_WORKSPACE.project,
    ...workspace.project,
    assignedAssetIds: workspace.assignedAssetIds || [],
    updatedAt: workspace.updatedAt || 0,
  })).sort((a, b) => b.updatedAt - a.updatedAt);
}

export function loadLocalWorkspace(projectId = DEFAULT_PROJECT_ID) {
  const saved = readLocalProjects()[projectId];
  return saved ? { ...EMPTY_WORKSPACE, ...saved, project: { ...EMPTY_WORKSPACE.project, ...saved.project } } : EMPTY_WORKSPACE;
}

export function saveLocalWorkspace(workspace, projectId = DEFAULT_PROJECT_ID) {
  const projects = readLocalProjects();
  projects[projectId] = {
    ...workspace,
    schemaVersion: WORKSPACE_SCHEMA_VERSION,
    updatedAt: Date.now(),
  };
  writeLocalProjects(projects);
}

export function createProjectId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `event-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export async function createWorkspace(user, project) {
  const projectId = createProjectId();
  const workspace = { ...EMPTY_WORKSPACE, project: { ...EMPTY_WORKSPACE.project, ...project } };
  await saveWorkspace(user, workspace, projectId);
  return projectId;
}

export function subscribeProjectList(user, onData, onError) {
  if (!user || user.isAnonymous) {
    onData(listLocalProjects());
    return () => {};
  }
  return onSnapshot(projectsCollection(user.uid), snapshot => {
    const projects = snapshot.docs.map(projectDoc => ({
      id: projectDoc.id,
      ...EMPTY_WORKSPACE.project,
      ...projectDoc.data().project,
      assignedAssetIds: projectDoc.data().assignedAssetIds || [],
      updatedAt: projectDoc.data().updatedAt || 0,
    })).sort((a, b) => b.updatedAt - a.updatedAt);
    onData(projects.length ? projects : listLocalProjects());
  }, onError);
}

export function subscribeWorkspace(user, onData, onError, projectId = DEFAULT_PROJECT_ID) {
  if (!user || user.isAnonymous) return () => {};
  return onSnapshot(workspaceRef(user.uid, projectId), snapshot => {
    if (snapshot.exists()) onData(snapshot.data());
  }, onError);
}

export async function saveWorkspace(user, workspace, projectId = DEFAULT_PROJECT_ID) {
  const payload = { ...workspace, schemaVersion: WORKSPACE_SCHEMA_VERSION, updatedAt: Date.now() };
  saveLocalWorkspace(payload, projectId);
  if (!user || user.isAnonymous) return;
  await setDoc(workspaceRef(user.uid, projectId), payload, { merge: true });
}
